import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  isStaleChunkError,
  isStaleChunkReloadStarted,
  reloadOnceForStaleChunk,
} from '../services/autoErrorReporter';

/**
 * React.lazy with a safe answer to "stale app files after a publish".
 *
 * An open tab from the previous version asks for hashed screen files that no longer exist.
 * Depending on the browser that shows up as a rejected import ("Failed to fetch dynamically
 * imported module", "Importing a module script failed", ...), or as an import that resolves
 * to nothing, so `module.ProcessNumbersModule` throws
 * "Cannot read properties of undefined (reading 'ProcessNumbersModule')".
 *
 * In all of those cases this reloads the page once (same sessionStorage once-guard as the
 * auto error reporter) and keeps the loading spinner up until the reload happens, instead of
 * crashing the screen. If a reload was already tried in the last few minutes it throws a clear
 * error, which the error boundary shows and the auto error reporter turns into a ticket.
 */

type LazyFactory<T extends ComponentType<any>> = () => Promise<{ default: T }>;

/** "module was undefined" style errors from `import(...).then((m) => m.Something)`. */
function isMissingModuleError(message: string): boolean {
  return /Cannot read propert(y|ies) of undefined|undefined is not an object|Cannot destructure property .* of .*undefined/i.test(
    message || ''
  );
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return String(err);
}

/** Never resolves: Suspense keeps showing its fallback while the page reloads. */
function waitForReload<T>(): Promise<T> {
  return new Promise<T>(() => undefined);
}

function handleStale<T>(reason: string): Promise<T> {
  if (isStaleChunkReloadStarted() || reloadOnceForStaleChunk()) {
    return waitForReload<T>();
  }
  throw new Error(
    `Failed to fetch dynamically imported module (the screen could not be loaded even after reloading the app). ${reason}`
  );
}

export function lazyWithReload<T extends ComponentType<any>>(factory: LazyFactory<T>): LazyExoticComponent<T> {
  return lazy(async () => {
    let loaded: { default: T } | undefined;
    try {
      loaded = await factory();
    } catch (err) {
      const message = messageOf(err);
      if (isStaleChunkError(message) || isMissingModuleError(message)) {
        return handleStale<{ default: T }>(message);
      }
      throw err;
    }
    if (!loaded || loaded.default === undefined || loaded.default === null) {
      return handleStale<{ default: T }>('The loaded module or its export was empty.');
    }
    return loaded;
  });
}